import { googleAdsSignupSendTo, safePageLocation } from '../util/googleTagIds';

export const SIGNUP_COMPLETE_PATH = '/signup-complete';

const PII_KEYS = /email|e-mail|name|phone|user_id|userId|firstName|lastName|address/i;

const withoutPii = params => {
  if (!params || typeof params !== 'object') {
    return {};
  }
  return Object.fromEntries(
    Object.entries(params).filter(([key, value]) => {
      if (PII_KEYS.test(key)) {
        return false;
      }
      if (typeof value === 'string' && value.includes('@')) {
        return false;
      }
      return value != null && value !== '';
    })
  );
};

export class LoggingAnalyticsHandler {
  trackPageView(url) {
    console.log('Analytics page view:', url); // eslint-disable-line no-console
  }

  trackEvent(eventName, params) {
    console.log('Analytics event:', eventName, withoutPii(params)); // eslint-disable-line no-console
  }
}

// Google Analytics 4 (GA4) using gtag.js script, which is included in util/includeScripts.js
export class GoogleAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    // GA4 property. Manually send page_view events
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    // Note 1: You should turn "Enhanced measurement" off.
    //         It attaches own listeners to elements and that breaks in-app navigation.
    // Note 2: If previousPath is null (just after page load), gtag script sends page_view event automatically.
    //         Only in-app navigation needs to be sent manually from SPA.
    // Note 3: Timeout is needed because gtag script picks up <title>,
    //         and location change event happens before initial rendering.
    if (previousPath && window.gtag) {
      window.setTimeout(() => {
        const page_location = safePageLocation(window.location.href);
        window.gtag('set', { page_location });
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
          page_location,
        });
      }, 300);
    }
  }

  /**
   * Send a named GA4 event with no emails, names, or user ids.
   *
   * @param {string} eventName
   * @param {Object} [params]
   */
  trackEvent(eventName, params) {
    if (!eventName || typeof window === 'undefined' || !window.gtag) {
      return;
    }
    const safeParams = withoutPii(params);
    window.gtag('event', eventName, safeParams);

    if (eventName === 'signup_completed') {
      // GA4 recommended event (import it as a conversion in Google Ads), plus a
      // direct Google Ads conversion when GOOGLE_ADS_SIGNUP_LABEL is configured.
      window.gtag('event', 'sign_up', safeParams.method ? { method: safeParams.method } : {});
      // Fires only after the account really exists (auth/signup or signupWithIdp
      // fulfilled), never on a /signup page view. Empty/invalid label = no call.
      const sendTo = googleAdsSignupSendTo();
      if (sendTo) {
        window.gtag('event', 'conversion', { send_to: sendTo });
      }
      // Virtual page view on a URL that only exists after a completed sign-up, so a
      // Google Ads "page load" URL rule can match "/signup-complete" (no label needed).
      try {
        const origin = window.location && window.location.origin;
        if (origin) {
          window.gtag('event', 'page_view', {
            page_location: `${origin}${SIGNUP_COMPLETE_PATH}`,
            page_path: SIGNUP_COMPLETE_PATH,
            page_title: 'Sign-up complete',
          });
        }
      } catch (e) {
        // never block sign-up on analytics
      }
    }
  }
}

export const sanitizeAnalyticsParams = withoutPii;

const SESSION_KEYS = ['booking_mode', 'topic'];
const sessionPixelParams = params => {
  const out = {};
  if (typeof params?.session_format === 'string') out.content_name = params.session_format;
  if (Array.isArray(params?.content_ids) && params.content_ids.length) {
    out.content_ids = params.content_ids.filter(id => typeof id === 'string');
    out.content_type = 'product';
  }
  SESSION_KEYS.forEach(k => {
    if (typeof params?.[k] === 'string' && params[k]) out[k] = params[k];
  });
  return out;
};

/**
 * Meta Pixel (fbevents.js), loaded from util/includeScripts.js only when
 * window.__META_PIXEL_ID__ / META_PIXEL_ID is set.
 *
 * PageView matches GA: the boot snippet sends the first one, and this
 * handler sends the rest on in-app navigation.
 *
 * CompleteRegistration is talker signup only (signup_role === 'talker').
 *
 * Schedule (not Purchase) fires on every confirmed booking: paid (value>0, paid:true)
 * and free (value:0, paid:false). Lead = non-talker signup. InitiateCheckout =
 * checkout_started (first speculate quote, deduped).
 */
export class MetaPixelHandler {
  trackPageView(canonicalPath, previousPath) {
    if (
      canonicalPath &&
      previousPath &&
      typeof window !== 'undefined' &&
      typeof window.fbq === 'function'
    ) {
      window.setTimeout(() => {
        window.fbq('track', 'PageView');
      }, 300);
    }
  }

  trackEvent(eventName, params) {
    if (!eventName || typeof window === 'undefined' || typeof window.fbq !== 'function') {
      return;
    }
    const methodMaybe =
      params?.method === 'email' || params?.method === 'sso' ? { method: params.method } : {};
    if (eventName === 'signup_completed') {
      if (params?.signup_role === 'talker') {
        window.fbq('track', 'CompleteRegistration', {
          status: true,
          content_name: 'talker',
          ...methodMaybe,
        });
      } else {
        window.fbq('track', 'Lead', { content_name: 'listener', ...methodMaybe });
      }
      return;
    }
    if (eventName === 'booking_completed') {
      const paid = params?.paid === true && typeof params?.value === 'number' && params.value > 0;
      window.fbq('track', 'Schedule', {
        value: paid ? params.value : 0,
        currency: 'USD',
        ...sessionPixelParams(params),
        paid,
      });
      return;
    }
    if (eventName === 'checkout_started') {
      window.fbq('track', 'InitiateCheckout', sessionPixelParams(params));
    }
  }
}
